/*
  Product lightbox (snippets/product-media-modal.liquid), still Dawn's product-modal / ModalDialog, so
  opening from the gallery, Escape, the scroll lock, the focus trap and returning focus to the gallery
  item that opened it all come from global.js.
  - One media item per screen in a horizontal scroll-snap track: swipe is native, arrows / thumbnails /
    Left and Right keys scroll it, and the counter and thumbnail highlight follow the scroll position.
  - Mouse: click an image (or the zoom tool) to zoom 2.2x; the zoomed image follows the pointer. The
    bigger source is requested from the image's own srcset only when zooming.
  - Touch: pinch or double-tap to zoom, one finger pans a zoomed image.
  - Full screen only where the browser supports it; share uses the Web Share API, else copies the link.
  - A click on the dark ground around the media closes the lightbox.
*/
if (!customElements.get('product-modal')) {
  const ZOOM = 2.2;
  const MAX_PINCH = 4;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement || null;

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.dialog = this.querySelector('[role="dialog"]');
        this.track = this.querySelector('.leza-lightbox__track');
        this.counter = this.querySelector('[data-leza-lb-counter]');
        this.thumbs = this.querySelector('[data-leza-lb-thumbs]');
        this.zoomButton = this.querySelector('[data-leza-lb-zoom]');
        this.fullscreenButton = this.querySelector('[data-leza-lb-fullscreen]');
        this.shareButton = this.querySelector('[data-leza-lb-share]');
        this.status = this.querySelector('[data-leza-lb-status]');
        this.arrows = [...this.querySelectorAll('[data-leza-lb-step]')];
        this.slides = [];
        this.index = 0;
        this.zoom = null;
        this.pointerType = 'mouse';
        this.frame = 0;

        this.arrows.forEach((arrow) =>
          arrow.addEventListener('click', () => this.go(this.index + Number(arrow.dataset.lezaLbStep)))
        );
        this.thumbs?.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-index]');
          if (thumb) this.go(Number(thumb.dataset.index));
        });
        this.zoomButton.addEventListener('click', () => this.toggleZoomFromButton());
        this.shareButton.addEventListener('click', () => this.share());

        if (document.fullscreenEnabled || document.webkitFullscreenEnabled) {
          this.fullscreenButton.hidden = false;
          this.fullscreenButton.addEventListener('click', () => this.toggleFullscreen());
          const sync = () => this.fullscreenButton.setAttribute('aria-pressed', String(fullscreenElement() === this));
          document.addEventListener('fullscreenchange', sync);
          document.addEventListener('webkitfullscreenchange', sync);
        } else {
          this.fullscreenButton.remove();
        }

        this.dialog.addEventListener('pointerdown', (event) => (this.pointerType = event.pointerType || 'mouse'));
        this.dialog.addEventListener('click', (event) => this.onClick(event));
        this.dialog.addEventListener('keydown', (event) => this.onKeydown(event));
        this.dialog.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.track.addEventListener('scroll', () => {
          if (!this.frame) this.frame = requestAnimationFrame(() => this.onScroll());
        });
        this.track.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: false });
        this.track.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false });
        this.track.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: false });
        this.track.addEventListener('touchcancel', (event) => this.onTouchEnd(event), { passive: false });
        if ('ResizeObserver' in window) {
          new ResizeObserver(() => {
            if (!this.hasAttribute('open')) return;
            this.unzoom(true);
            this.track.scrollLeft = this.index * this.track.clientWidth;
          }).observe(this.track);
        }
      }

      show(opener) {
        this.slides = [...this.track.querySelectorAll('[data-leza-slide]')];
        // Built before the focus trap is set up, so the trap knows the last thumbnail.
        this.buildThumbs();
        super.show(opener);
        const id = opener?.getAttribute('data-media-id');
        const start = Math.max(
          0,
          this.slides.findIndex((slide) => slide.dataset.lezaSlide === id)
        );
        this.track.scrollLeft = start * this.track.clientWidth;
        this.setIndex(start, true);

        const deferred = this.slides[start]?.querySelector('deferred-media');
        if (deferred && deferred.querySelector('template')?.content.querySelector('.js-youtube')) deferred.loadContent();
      }

      hide() {
        this.unzoom(true);
        if (fullscreenElement() === this) {
          (document.exitFullscreen || document.webkitExitFullscreen)?.call(document)?.catch?.(() => {});
        }
        super.hide();
      }

      /* ---------- Navigation ---------- */

      buildThumbs() {
        if (!this.thumbs) return;
        const key = this.slides.map((slide) => slide.dataset.lezaSlide).join(',');
        if (this.thumbs.dataset.key === key) return;
        this.thumbs.dataset.key = key;
        this.thumbs.replaceChildren(
          ...this.slides.map((slide, i) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'leza-lightbox__thumb';
            button.dataset.index = String(i);
            button.setAttribute('aria-label', this.thumbs.dataset.label.replace('[index]', i + 1));
            const image = document.createElement('img');
            image.src = slide.dataset.thumb;
            image.alt = '';
            image.width = 80;
            image.height = 80;
            image.loading = 'lazy';
            image.decoding = 'async';
            button.append(image);
            return button;
          })
        );
      }

      go(target) {
        const next = clamp(target, 0, this.slides.length - 1);
        if (next === this.index) return;
        this.unzoom(true);
        this.track.scrollTo({ left: next * this.track.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });
        this.setIndex(next);
      }

      onScroll() {
        this.frame = 0;
        if (this.zoom || !this.track.clientWidth) return;
        const index = clamp(Math.round(this.track.scrollLeft / this.track.clientWidth), 0, this.slides.length - 1);
        if (index !== this.index) this.setIndex(index);
      }

      setIndex(index, force = false) {
        if (index === this.index && !force) return;
        const changed = index !== this.index;
        this.index = index;
        const total = this.slides.length;
        this.counter.textContent = `${index + 1} / ${total}`;

        const focused = document.activeElement;
        const [prev, next] = this.arrows;
        if (prev && next) {
          prev.disabled = index <= 0;
          next.disabled = index >= total - 1;
          // Keep keyboard focus inside the dialog when the pressed arrow reaches its end.
          if (focused === prev && prev.disabled) next.focus();
          if (focused === next && next.disabled) prev.focus();
        }

        const isImage = this.slides[index]?.classList.contains('leza-lightbox__slide--image');
        this.zoomButton.disabled = !isImage;

        if (this.thumbs) {
          [...this.thumbs.children].forEach((thumb, i) => {
            if (i === index) thumb.setAttribute('aria-current', 'true');
            else thumb.removeAttribute('aria-current');
          });
          const thumb = this.thumbs.children[index];
          if (thumb) {
            const left = thumb.offsetLeft - (this.thumbs.clientWidth - thumb.offsetWidth) / 2;
            this.thumbs.scrollTo({ left, behavior: force || reduceMotion() ? 'auto' : 'smooth' });
          }
        }
        if (changed && !force) window.pauseAllMedia();
      }

      onKeydown(event) {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          if (event.target.closest('input, textarea, select, model-viewer, iframe')) return;
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
          return;
        }
        if (event.key !== 'Tab') return;
        // Dawn's focus trap counts controls hidden at this width (e.g. the zoom tool on phones): wrap
        // between the first and last controls that are actually shown.
        const focusable = [...this.dialog.querySelectorAll('button:enabled, [href], [tabindex]:not([tabindex="-1"])')].filter(
          (element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const target = event.target;
        if (!event.shiftKey && target === last) {
          event.preventDefault();
          event.stopPropagation();
          first.focus();
        } else if (event.shiftKey && (target === first || target === this.dialog)) {
          event.preventDefault();
          event.stopPropagation();
          last.focus();
        }
      }

      onClick(event) {
        const target = event.target;
        if (target.closest('button, a, deferred-media, product-model, model-viewer, .leza-lightbox__bar, .leza-lightbox__thumbs')) return;
        if (this.zoom) {
          // A touch zoom is left with a double-tap or by pinching back out.
          if (this.pointerType === 'mouse') this.unzoom();
          return;
        }
        const slide = target.closest('.leza-lightbox__slide--image');
        if (slide && target.tagName === 'IMG') {
          // Touch zooms with pinch / double-tap; a single tap on the photo does nothing.
          if (this.pointerType === 'mouse') this.zoomMouse(slide, event.clientX, event.clientY);
          return;
        }
        // The dark ground around the media.
        this.hide();
      }

      /* ---------- Zoom ---------- */

      image(slide) {
        return slide?.querySelector('img');
      }

      requestSharper(image, scale, width) {
        const needed = Math.ceil(width * scale);
        const current = parseInt(image.dataset.lezaZoomSizes || '0', 10);
        if (needed <= current) return;
        image.dataset.lezaZoomSizes = String(needed);
        image.sizes = `${needed}px`;
      }

      startZoom(slide, mode) {
        const image = this.image(slide);
        if (!image) return null;
        const base = image.getBoundingClientRect();
        this.zoom = { slide, image, mode, scale: 1, tx: 0, ty: 0, base };
        slide.classList.add('is-zoomed');
        this.dialog.classList.add('is-zoomed');
        this.zoomButton.setAttribute('aria-pressed', 'true');
        return this.zoom;
      }

      zoomMouse(slide, x, y) {
        const zoom = this.startZoom(slide, 'mouse');
        if (!zoom) return;
        zoom.scale = ZOOM;
        this.requestSharper(zoom.image, ZOOM, zoom.base.width);
        this.setOrigin(x, y);
        zoom.image.style.transform = `scale(${ZOOM})`;
      }

      setOrigin(x, y) {
        const { base, image } = this.zoom;
        const px = clamp(((x - base.left) / base.width) * 100, 0, 100);
        const py = clamp(((y - base.top) / base.height) * 100, 0, 100);
        image.style.transformOrigin = `${px}% ${py}%`;
      }

      onPointerMove(event) {
        if (this.zoom?.mode !== 'mouse' || event.pointerType !== 'mouse') return;
        const { clientX, clientY } = event;
        if (this.moveFrame) return;
        this.moveFrame = requestAnimationFrame(() => {
          this.moveFrame = 0;
          if (this.zoom?.mode === 'mouse') this.setOrigin(clientX, clientY);
        });
      }

      toggleZoomFromButton() {
        if (this.zoom) {
          this.unzoom();
          return;
        }
        const slide = this.slides[this.index];
        if (!slide?.classList.contains('leza-lightbox__slide--image')) return;
        const image = this.image(slide);
        if (!image) return;
        const rect = image.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;
        if (this.pointerType === 'mouse') this.zoomMouse(slide, x, y);
        else this.zoomTouch(slide, ZOOM, x, y);
      }

      // Touch model: translate(tx, ty) scale(s) from the top-left corner, kept inside the slide.
      applyTouch() {
        const zoom = this.zoom;
        const { base, image, slide } = zoom;
        const frame = slide.getBoundingClientRect();
        const width = base.width * zoom.scale;
        const height = base.height * zoom.scale;
        const left = base.left - frame.left;
        const top = base.top - frame.top;
        zoom.tx =
          width >= frame.width
            ? clamp(zoom.tx, frame.width - left - width, -left)
            : (frame.width - width) / 2 - left;
        zoom.ty =
          height >= frame.height
            ? clamp(zoom.ty, frame.height - top - height, -top)
            : (frame.height - height) / 2 - top;
        image.style.transformOrigin = '0 0';
        image.style.transform = `translate(${zoom.tx}px, ${zoom.ty}px) scale(${zoom.scale})`;
      }

      zoomTouch(slide, scale, x, y) {
        const zoom = this.startZoom(slide, 'touch');
        if (!zoom) return;
        zoom.scale = scale;
        zoom.tx = (x - zoom.base.left) * (1 - scale);
        zoom.ty = (y - zoom.base.top) * (1 - scale);
        this.requestSharper(zoom.image, scale, zoom.base.width);
        this.applyTouch();
      }

      unzoom(instant = false) {
        const zoom = this.zoom;
        if (!zoom) return;
        this.zoom = null;
        zoom.slide.classList.remove('is-zoomed');
        this.dialog.classList.remove('is-zoomed', 'is-gesturing');
        this.zoomButton.setAttribute('aria-pressed', 'false');
        if (instant) zoom.image.style.transition = 'none';
        zoom.image.style.transform = '';
        if (instant) {
          zoom.image.getBoundingClientRect();
          zoom.image.style.transition = '';
          zoom.image.style.transformOrigin = '';
        }
      }

      /* ---------- Touch: pinch, pan, double-tap ---------- */

      onTouchStart(event) {
        const slide = this.slides[this.index];
        const touches = event.touches;
        if (touches.length === 2 && slide?.classList.contains('leza-lightbox__slide--image')) {
          event.preventDefault();
          if (this.zoom && this.zoom.mode !== 'touch') this.unzoom(true);
          const zoom = this.zoom || this.startZoom(slide, 'touch');
          if (!zoom) return;
          const [a, b] = touches;
          this.gesture = {
            type: 'pinch',
            distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1,
            x: (a.clientX + b.clientX) / 2,
            y: (a.clientY + b.clientY) / 2,
            scale: zoom.scale,
            tx: zoom.tx,
            ty: zoom.ty,
          };
          this.dialog.classList.add('is-gesturing');
          return;
        }
        if (touches.length === 1) {
          const touch = touches[0];
          this.tap = { x: touch.clientX, y: touch.clientY, time: event.timeStamp, moved: false };
          if (this.zoom?.mode === 'touch') {
            this.gesture = { type: 'pan', x: touch.clientX, y: touch.clientY, tx: this.zoom.tx, ty: this.zoom.ty };
            this.dialog.classList.add('is-gesturing');
          }
        }
      }

      onTouchMove(event) {
        const touches = event.touches;
        if (this.tap && touches.length === 1) {
          const touch = touches[0];
          if (Math.hypot(touch.clientX - this.tap.x, touch.clientY - this.tap.y) > 10) this.tap.moved = true;
        }
        const gesture = this.gesture;
        if (!gesture || !this.zoom) return;
        if (gesture.type === 'pinch' && touches.length === 2) {
          event.preventDefault();
          const [a, b] = touches;
          const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
          const x = (a.clientX + b.clientX) / 2;
          const y = (a.clientY + b.clientY) / 2;
          const zoom = this.zoom;
          const scale = clamp((gesture.scale * distance) / gesture.distance, 1, MAX_PINCH);
          // Keep the point that started under the fingers under the fingers.
          const localX = (gesture.x - zoom.base.left - gesture.tx) / gesture.scale;
          const localY = (gesture.y - zoom.base.top - gesture.ty) / gesture.scale;
          zoom.scale = scale;
          zoom.tx = x - zoom.base.left - scale * localX;
          zoom.ty = y - zoom.base.top - scale * localY;
          this.applyTouch();
        } else if (gesture.type === 'pan' && touches.length === 1) {
          event.preventDefault();
          const touch = touches[0];
          this.zoom.tx = gesture.tx + touch.clientX - gesture.x;
          this.zoom.ty = gesture.ty + touch.clientY - gesture.y;
          this.applyTouch();
        }
      }

      onTouchEnd(event) {
        const gesture = this.gesture;
        if (gesture?.type === 'pinch' && event.touches.length < 2) {
          this.gesture = null;
          this.dialog.classList.remove('is-gesturing');
          if (!this.zoom) return;
          if (this.zoom.scale < 1.05) {
            this.unzoom();
            return;
          }
          this.requestSharper(this.zoom.image, this.zoom.scale, this.zoom.base.width);
          const rest = event.touches[0];
          if (rest) this.gesture = { type: 'pan', x: rest.clientX, y: rest.clientY, tx: this.zoom.tx, ty: this.zoom.ty };
          return;
        }
        if (gesture?.type === 'pan' && event.touches.length === 0) {
          this.gesture = null;
          this.dialog.classList.remove('is-gesturing');
        }

        // Double-tap: zoom in at the tap, or back out.
        const tap = this.tap;
        this.tap = null;
        if (!tap || tap.moved || event.touches.length || event.timeStamp - tap.time > 300) return;
        const slide = this.slides[this.index];
        const onImage = event.target.tagName === 'IMG' && slide?.classList.contains('leza-lightbox__slide--image');
        const last = this.lastTap;
        if (last && event.timeStamp - last.time < 320 && Math.hypot(tap.x - last.x, tap.y - last.y) < 30) {
          this.lastTap = null;
          if (!onImage && !this.zoom) return;
          event.preventDefault();
          if (this.zoom) this.unzoom();
          else this.zoomTouch(slide, ZOOM, tap.x, tap.y);
          return;
        }
        this.lastTap = { x: tap.x, y: tap.y, time: event.timeStamp };
      }

      /* ---------- Full screen and share ---------- */

      toggleFullscreen() {
        if (fullscreenElement()) {
          (document.exitFullscreen || document.webkitExitFullscreen).call(document)?.catch?.(() => {});
          return;
        }
        const request = this.requestFullscreen || this.webkitRequestFullscreen;
        try {
          request.call(this)?.catch?.(() => {});
        } catch (error) {
          // Refused (e.g. inside an iframe without permission): nothing to undo.
        }
      }

      async share() {
        const url = this.shareButton.dataset.url || window.location.href;
        const title = this.shareButton.dataset.title || document.title;
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
            return;
          } catch (error) {
            if (error?.name === 'AbortError') return;
          }
        }
        try {
          await navigator.clipboard.writeText(url);
          this.say(this.shareButton.dataset.copied);
        } catch (error) {
          this.say(url);
        }
      }

      say(message) {
        clearTimeout(this.sayTimer);
        this.status.textContent = message;
        this.sayTimer = setTimeout(() => (this.status.textContent = ''), 2400);
      }
    }
  );
}
